/**
 * Is an APS document's 3D geometry ready to load?
 *
 * When a model's translation failed or is still running, its default geometry has no viewable
 * URN. APS's loadDocumentNode then throws inside its own promise and never settles, so the 3D
 * viewer would show "Loading… 0%" forever. The viewer checks with getModelNotReady first and
 * rejects with a MODEL_NOT_READY error instead; the screens show the message once with
 * notifyIfModelNotReady.
 *
 * No imports on purpose: this file is unit-tested under plain Node.
 */

export const MODEL_NOT_READY = 'MODEL_NOT_READY';

export const MODEL_NOT_READY_STATUS = Object.freeze({
  FAILED: 'failed',
  IN_PROGRESS: 'inprogress',
});

export const MODEL_NOT_READY_MESSAGES = Object.freeze({
  [MODEL_NOT_READY_STATUS.FAILED]:
    "This facility's 3D model couldn't be processed. Upload it again or choose another version in Models.",
  [MODEL_NOT_READY_STATUS.IN_PROGRESS]: 'The 3D model is still being processed. Try again in a few minutes.',
});

const NOTIFY_LEVEL = Object.freeze({
  [MODEL_NOT_READY_STATUS.FAILED]: 'error',
  [MODEL_NOT_READY_STATUS.IN_PROGRESS]: 'warning',
});

const NOTIFY_OPTIONS = Object.freeze({ position: 'top-right', timeout: 8000 });

// APS Model Derivative manifest statuses. A manifest without a viewable that is neither of these
// (e.g. success with nothing loadable) cannot be shown either, so it counts as failed.
const MANIFEST_STATUSES_FAILED = Object.freeze(['failed', 'timeout']);
const MANIFEST_STATUSES_IN_PROGRESS = Object.freeze(['pending', 'inprogress']);

function isInProgress(status) {
  return MANIFEST_STATUSES_IN_PROGRESS.includes(status);
}

function resolveNotReadyStatus(manifestStatus, geometryStatus) {
  // The manifest root decides first: a failed manifest can still hold a geometry node that says
  // "inprogress", and that model will never finish.
  if (MANIFEST_STATUSES_FAILED.includes(manifestStatus)) return MODEL_NOT_READY_STATUS.FAILED;
  if (isInProgress(manifestStatus) || isInProgress(geometryStatus)) return MODEL_NOT_READY_STATUS.IN_PROGRESS;
  return MODEL_NOT_READY_STATUS.FAILED;
}

/**
 * @param {object} doc - APS Autodesk.Viewing.Document
 * @param {object|null|undefined} geometry - doc.getRoot().getDefaultGeometry()
 * @returns {null|{status: 'failed'|'inprogress'}} null when the geometry can be loaded
 */
export function getModelNotReady(doc, geometry) {
  const viewableUrn = geometry ? doc.getViewableUrn(geometry) : null;
  if (viewableUrn) return null;
  const manifestStatus = doc.getRoot()?.data?.status;
  return { status: resolveNotReadyStatus(manifestStatus, geometry?.data?.status) };
}

export function createModelNotReadyError(status) {
  return Object.assign(new Error(MODEL_NOT_READY_MESSAGES[status]), { code: MODEL_NOT_READY, status });
}

/**
 * Shows the not-ready message when err is a MODEL_NOT_READY error.
 * @returns {boolean} true when it showed the message; any other error is left to the caller
 */
export function notifyIfModelNotReady(err, dialog) {
  if (err?.code !== MODEL_NOT_READY) return false;
  dialog.notify[NOTIFY_LEVEL[err.status]](err.message, NOTIFY_OPTIONS);
  return true;
}
